import type {
  PassageAuthoring,
  PassageDetail,
  PassageImportPayload,
  PassageLexical,
  PassageSentence,
  PassageTermRef,
  Taxonomy,
} from '../api/types'
import { LEXICAL_TYPES, type LexicalType } from '../../lexicals/api/types.ts'

const EMPTY_SENTENCE: PassageSentence = {
  text: '',
  tokens: [],
  translations: {},
  phonemes: null,
  audio: null,
  image: null,
  lexicals: [],
}

export const EMPTY_PASSAGE: PassageImportPayload = {
  passage: {
    title: EMPTY_SENTENCE,
    summary: null,
    difficulty: null,
    reward_points: 0,
    image: null,
    visual_bible: null,
    terms: [],
    activities: [],
    paragraphs: [],
    replace_paragraphs: true,
    replace_activities: true,
  },
}

export function createEmptyPassageJson(): string {
  return JSON.stringify(EMPTY_PASSAGE, null, 2)
}

function lexicalFromDetail(mapping: PassageDetail['title']['lexicals'][number]): PassageLexical {
  return {
    ...mapping.lexical,
    mapping_id: mapping.id,
    position: mapping.position ?? undefined,
    token_indexes: mapping.token_indexes,
  }
}

function sentenceFromDetail(sentence: PassageDetail['title']): PassageSentence {
  return {
    id: sentence.id,
    text: sentence.text,
    tokens: sentence.tokens,
    translations: sentence.translations,
    phonemes: sentence.phonemes,
    pronunciations: sentence.pronunciations ?? [],
    audio: sentence.audio,
    image: sentence.image,
    lexicals: sentence.lexicals.map(lexicalFromDetail),
  }
}

export function detailToImportPayload(detail: PassageDetail): PassageImportPayload {
  const passage: PassageAuthoring = {
    id: detail.id,
    title: sentenceFromDetail(detail.title),
    summary: detail.summary,
    difficulty: detail.difficulty,
    reward_points: detail.reward_points,
    image: detail.image,
    visual_bible: detail.visual_bible,
    terms: detail.terms.map((term) => ({
      taxonomy_code: term.taxonomy.code,
      code: term.code,
    })),
    activities: detail.activities.map((activity) => ({ ...activity })),
    paragraphs: detail.paragraphs.map((paragraph) => ({
      id: paragraph.id,
      position: paragraph.position,
      image: paragraph.image,
      sentences: paragraph.sentences.map(({ id, position, sentence }) => ({
        ...sentenceFromDetail(sentence),
        paragraph_sentence_id: id,
        position,
      })),
    })),
    replace_paragraphs: true,
    replace_activities: true,
  }
  return { passage }
}

export function parsePassageJson(value: string): PassageImportPayload {
  const parsed: unknown = JSON.parse(value)
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    throw new Error('JSON phải là một object.')
  }
  const payload = parsed as { passage?: unknown }
  if (!payload.passage || typeof payload.passage !== 'object' || Array.isArray(payload.passage)) {
    throw new Error('JSON cần có object passage.')
  }
  return parsed as PassageImportPayload
}

export function passageStats(payload: PassageImportPayload | null) {
  if (!payload) return { paragraphs: 0, sentences: 0, lexicals: 0, activities: 0 }
  const sentences = payload.passage.paragraphs.flatMap((paragraph) => paragraph.sentences)
  return {
    paragraphs: payload.passage.paragraphs.length,
    sentences: sentences.length + (payload.passage.title.text.trim() ? 1 : 0),
    lexicals: payload.passage.title.lexicals.length + sentences.reduce((sum, sentence) => sum + sentence.lexicals.length, 0),
    activities: payload.passage.activities.length,
  }
}

/**
 * Keep a single, stable apostrophe form in persisted tokens.  Rich-text editors
 * and AI responses commonly use typographic apostrophes; without normalising
 * them, a possessive can stop matching its lexical and pronunciation mappings.
 */
export function normalizeTokenText(text: string): string {
  return text.replace(/[‘’‛ʼ＇]/gu, "'")
}

export function tokenizeSentence(text: string): string[] {
  return normalizeTokenText(text).match(/[\p{L}\p{N}]+(?:['-][\p{L}\p{N}]+)*|[^\s]/gu) ?? []
}

export function textFromTokenIndexes(tokens: string[], indexes: number[]): string {
  const selected = [...new Set(indexes)].sort((left, right) => left - right).map((index) => tokens[index]).filter(Boolean)
  return selected.join(' ')
    .replace(/\s+([,.;:!?%)\]}])/gu, '$1')
    .replace(/([([{])\s+/gu, '$1')
}

export function parseAiJson(value: string): Record<string, unknown> {
  const trimmed = value.trim()
  const withoutFence = trimmed.startsWith('```')
    ? trimmed.replace(/^```(?:json)?\s*/iu, '').replace(/\s*```$/u, '')
    : trimmed
  const parsed: unknown = JSON.parse(withoutFence)
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) throw new Error('AI JSON phải là một object.')
  return parsed as Record<string, unknown>
}

function objectValue(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : null
}

function aiSentence(value: unknown, label: string): PassageSentence {
  const sentence = objectValue(value)
  const text = typeof sentence?.text === 'string' ? sentence.text.trim() : ''
  const translations = objectValue(sentence?.translations)
  const vi = typeof sentence?.translation_vi === 'string'
    ? sentence.translation_vi.trim()
    : typeof translations?.vi === 'string'
      ? translations.vi.trim()
      : ''
  if (!text) throw new Error(`${label} thiếu nội dung tiếng Anh.`)
  if (!vi) throw new Error(`${label} thiếu bản dịch tiếng Việt.`)
  return { text, tokens: tokenizeSentence(text), translations: { vi }, phonemes: null, audio: null, image: null, lexicals: [] }
}

export function prepareAiPassagePayload(value: string, terms: PassageTermRef[]): PassageImportPayload {
  const parsed = parseAiJson(value)
  const source = objectValue(parsed.passage) ?? parsed
  const sourceTerms: PassageTermRef[] = Array.isArray(source.terms)
    ? source.terms.flatMap<PassageTermRef>((value) => {
      const term = objectValue(value)
      const termId = typeof term?.term_id === 'string' && term.term_id.trim() ? term.term_id.trim() : null
      const taxonomyCode = typeof term?.taxonomy_code === 'string' && term.taxonomy_code.trim() ? term.taxonomy_code.trim() : null
      const code = typeof term?.code === 'string' && term.code.trim() ? term.code.trim() : null
      if (termId) return [{ term_id: termId }]
      return taxonomyCode && code ? [{ taxonomy_code: taxonomyCode, code }] : []
    })
    : []
  const rawParagraphs = source.paragraphs
  if (!Array.isArray(rawParagraphs) || rawParagraphs.length === 0) throw new Error('AI JSON cần ít nhất một paragraph.')
  const paragraphs = rawParagraphs.map((rawParagraph, paragraphIndex) => {
    const paragraph = objectValue(rawParagraph)
    if (!paragraph || !Array.isArray(paragraph.sentences) || paragraph.sentences.length === 0) {
      throw new Error(`Paragraph ${paragraphIndex + 1} cần ít nhất một sentence.`)
    }
    return {
      position: paragraphIndex,
      image: null,
      sentences: paragraph.sentences.map((sentence, sentenceIndex) => aiSentence(sentence, `Paragraph ${paragraphIndex + 1}, sentence ${sentenceIndex + 1}`)),
    }
  })
  const summary = typeof source.summary === 'string' && source.summary.trim() ? source.summary.trim() : null
  const difficulty = Number.isSafeInteger(source.difficulty) ? source.difficulty as number : null
  return {
    passage: {
      title: aiSentence(source.title, 'Title'),
      summary,
      difficulty,
      reward_points: 0,
      image: null,
      visual_bible: null,
      terms: terms.length > 0 ? terms : sourceTerms,
      activities: [],
      paragraphs,
      replace_paragraphs: true,
      replace_activities: true,
    },
  }
}

export function buildPassageDraftPrompt(taxonomies: Taxonomy[], selectedTermIds: string[], description: string): string {
  const selected = taxonomies.flatMap((taxonomy) => taxonomy.terms
    .filter((term) => selectedTermIds.includes(term.id))
    .map((term) => ({
      taxonomy: taxonomy.name,
      taxonomy_description: taxonomy.description,
      term: term.name,
      term_description: term.description,
    })))
  return `Viết một bài đọc tiếng Anh cho người học theo brief và phân loại bên dưới. Chỉ trả về một JSON object, không markdown, không giải thích.

Brief của biên tập viên:
${description.trim()}

Phân loại đã chọn:
${JSON.stringify(selected, null, 2)}

Yêu cầu nội dung:
- Nội dung đúng với brief và các phân loại; chia thành các paragraph, mỗi paragraph chia thành sentence hoàn chỉnh.
- Title cũng là một sentence và có bản dịch.
- Mỗi sentence phải có đúng một bản dịch tiếng Việt sát nghĩa theo ngữ cảnh, không diễn giải nghĩa bóng và không liệt kê nhiều phương án.
- Giữ cách xưng hô, tên gọi và cách dịch thuật ngữ nhất quán xuyên suốt. Ví dụ đã dùng “tớ/cậu” thì không đổi thành “tôi/bạn”.
- Không phân tích lexical ở bước này.
- Chỉ viết summary sau khi đã hoàn thành toàn bộ bài đọc; summary phải được rút ra từ nội dung thực tế của bài.
- summary gồm 1–2 câu tiếng Anh, tóm tắt nhân vật/sự vật, diễn biến hoặc kiến thức chính để người đọc hiểu bài nói về điều gì.
- Không nhắc lại CEFR, độ khó, taxonomy, thể loại, đối tượng học, brief hay quá trình biên soạn trong summary.
- difficulty là một số nguyên tùy chọn; dùng null nếu chưa đủ cơ sở chấm.

JSON shape bắt buộc:
{"title":{"text":"English title","translation_vi":"Bản dịch sát nghĩa"},"summary":"Short English summary","difficulty":null,"paragraphs":[{"sentences":[{"text":"English sentence.","translation_vi":"Bản dịch sát nghĩa."}]}]}`
}

export function buildDifficultyAssessmentPrompt(passage: PassageAuthoring): string {
  const text = [
    passage.title.text,
    ...passage.paragraphs.map((paragraph) => paragraph.sentences.map((sentence) => sentence.text).join(' ')),
  ].filter(Boolean).join('\n\n')
  return `Assess the English reading difficulty of the passage below. Return exactly one integer and nothing else.

This product uses an internal numeric scale inspired by the practical range and progression of common English reading measures. It is not an official Lexile measure and must not claim to be one. Use an open-ended non-negative integer where approximate anchors are: 0–200 for emergent readers, 200–500 for early elementary texts, 500–800 for developing readers, 800–1100 for intermediate school-level texts, 1100–1400 for advanced school-level texts, and 1400+ for highly complex academic texts.

Judge the complete passage, not any taxonomy or translation. Balance word frequency and precision, sentence length, syntactic complexity, cohesion, implied meaning, and conceptual load. Do not score from topic familiarity alone. Give the single best estimate; do not provide a range, explanation, label, unit, JSON, or markdown.

PASSAGE:\n${text}`
}

export function buildSentenceAnalysisPrompt(passageText: string, sentenceText: string): string {
  return `Phân tích đúng một câu tiếng Anh trong ngữ cảnh bài đọc dưới đây. Chỉ trả về JSON object, không markdown và không giải thích.

Ngữ cảnh bài đọc:
${passageText}

Câu cần phân tích:
${sentenceText}

Yêu cầu:
- translations.vi là một bản dịch tự nhiên, đúng duy nhất với ngữ cảnh này.
- phonemes chỉ ghi khi cách đọc của cả câu thực sự mơ hồ; nếu không phải null.

Tokens được hệ thống tách cố định trực tiếp từ text, không cần trả về tokens.

JSON shape:
{"translations":{"vi":""},"phonemes":null}`
}

export function buildLexicalAnalysisPrompt(sentenceText: string, lexicalText: string, type: string): string {
  return `Phân tích đúng một lexical tiếng Anh trong câu. Chỉ trả về JSON object, không markdown và không giải thích.

Câu ngữ cảnh: ${sentenceText}
Lexical: ${lexicalText}
Type dự kiến: ${type}

Yêu cầu:
- text giữ đúng lexical trong câu.
- type chỉ là vocabulary, phrase, collocation, phrasal_verb, idiom hoặc pattern.
- translations.vi chỉ có một nghĩa ngắn gọn, đúng với câu này; không liệt kê nghĩa từ điển.
- phonemes chỉ ghi khi cách đọc mơ hồ; nếu không phải null.

JSON shape:
{"text":"${lexicalText}","type":"${type}","translations":{"vi":""},"phonemes":null}`
}

export interface AiSentenceLexical {
  token_indexes: number[]
  text: string
  type: LexicalType
  translations: Record<string, string>
  phonemes: string | null
}

export interface AiExistingLexical extends Omit<AiSentenceLexical, 'token_indexes'> {
  id: string
}

export function buildExistingSentenceLexicalsPrompt(sentence: PassageSentence, lexicals: PassageLexical[]): string {
  const selected = lexicals.map((lexical) => ({
    id: lexical.id,
    text: lexical.text,
    type: lexical.type,
    token_indexes: lexical.token_indexes ?? [],
    current_translation_vi: lexical.translations.vi ?? null,
    current_phonemes: lexical.phonemes,
  }))
  return `Complete the teaching data for all selected English lexicals in one sentence. Return only one JSON object, with no markdown or explanation.

Sentence context: ${sentence.text}
Selected lexicals: ${JSON.stringify(selected)}

Return exactly one result for every selected id. Preserve each id and text exactly. Choose the most appropriate type for the sentence context. translations.vi must be one short Vietnamese meaning specific to this context; never list alternatives. phonemes must be null unless pronunciation is genuinely ambiguous. Do not include audio, image, token indexes, commentary, or any unselected lexical.

Required JSON shape:
{"lexicals":[{"id":"selected lexical id","text":"original text","type":"vocabulary","translations":{"vi":"nghĩa theo ngữ cảnh"},"phonemes":null}]}`
}

export function parseAiExistingLexicals(value: string, expected: PassageLexical[]): AiExistingLexical[] {
  const parsed = parseAiJson(value)
  if (!Array.isArray(parsed.lexicals) || parsed.lexicals.length !== expected.length) throw new Error('AI JSON phải có đúng một kết quả cho mỗi lexical đã chọn.')
  const expectedById = new Map(expected.filter((lexical) => lexical.id).map((lexical) => [lexical.id!, lexical]))
  const seen = new Set<string>()
  return parsed.lexicals.map((raw, index) => {
    const item = objectValue(raw)
    const id = typeof item?.id === 'string' ? item.id : ''
    const source = expectedById.get(id)
    if (!source || seen.has(id)) throw new Error(`Lexical ${index + 1} có id không thuộc danh sách đã chọn.`)
    seen.add(id)
    const text = typeof item?.text === 'string' ? item.text.trim() : ''
    if (text !== source.text) throw new Error(`Lexical ${index + 1} phải giữ nguyên text “${source.text}”.`)
    const type = typeof item?.type === 'string' && LEXICAL_TYPES.includes(item.type as LexicalType) ? item.type as LexicalType : null
    if (!type) throw new Error(`Lexical ${index + 1} có type không hợp lệ.`)
    const translations = objectValue(item?.translations)
    const vi = typeof translations?.vi === 'string' ? translations.vi.trim() : ''
    if (!vi) throw new Error(`Lexical ${index + 1} thiếu translations.vi.`)
    const phonemes = item?.phonemes === null ? null : typeof item?.phonemes === 'string' ? item.phonemes.trim() || null : null
    return { id, text, type, translations: { vi }, phonemes }
  })
}

export function buildSentenceLexicalsPrompt(sentence: PassageSentence): string {
  const tokens = sentence.tokens ?? tokenizeSentence(sentence.text)
  const existing = sentence.lexicals.map((lexical) => ({
    token_indexes: lexical.token_indexes ?? [],
    text: lexical.text,
    type: lexical.type,
    translation_vi: lexical.translations.vi ?? '',
  }))
  return `Select useful English lexicals to teach from exactly one sentence. Return only a JSON object, with no markdown or explanation.

Sentence: ${sentence.text}
Tokens with immutable zero-based indexes: ${JSON.stringify(tokens.map((text, index) => ({ index, text })))}
Existing lexical mappings (do not repeat an identical mapping): ${JSON.stringify(existing)}

Choose only meaningful vocabulary, phrases, collocations, phrasal verbs, idioms, or patterns worth studying in this exact context. A lexical may overlap another lexical, such as both “car” and “a blue car”. Do not create entries for punctuation, function words alone, arbitrary fragments, or every word in the sentence.

For each item, token_indexes must refer only to the listed indexes and text must exactly reconstruct from those tokens in order. translations.vi must contain one short Vietnamese meaning specific to this context; never list dictionary alternatives. phonemes must be null unless pronunciation is genuinely ambiguous.

Required JSON shape:
{"lexicals":[{"token_indexes":[0],"text":"word or phrase","type":"vocabulary","translations":{"vi":"nghĩa theo ngữ cảnh"},"phonemes":null}]}`
}

export function buildSelectedSentenceLexicalsPrompt(sentence: PassageSentence, selections: number[][]): string {
  const tokens = sentence.tokens ?? tokenizeSentence(sentence.text)
  const targets = selections.map((tokenIndexes) => ({ token_indexes: tokenIndexes, text: textFromTokenIndexes(tokens, tokenIndexes) }))
  return `Complete the teaching data for exactly the lexical targets selected by an editor. Return only one JSON object, with no markdown or explanation.

Sentence: ${sentence.text}
Tokens with immutable zero-based indexes: ${JSON.stringify(tokens.map((text, index) => ({ index, text })))}
Editor-selected lexical targets: ${JSON.stringify(targets)}

Return exactly one item for every selected target. Preserve each token_indexes and text exactly. Choose the most appropriate type for this sentence context. translations.vi must contain one short Vietnamese meaning specific to this context; never list alternatives. phonemes must be null unless pronunciation is genuinely ambiguous.

Required JSON shape:
{"lexicals":[{"token_indexes":[0],"text":"word or phrase","type":"vocabulary","translations":{"vi":"nghĩa theo ngữ cảnh"},"phonemes":null}]}`
}

export function parseAiSentenceLexicals(value: string, tokens: string[]): AiSentenceLexical[] {
  const parsed = parseAiJson(value)
  if (!Array.isArray(parsed.lexicals) || parsed.lexicals.length === 0) throw new Error('AI JSON cần có mảng lexicals không rỗng.')
  const seen = new Set<string>()
  return parsed.lexicals.map((raw, index) => {
    const item = objectValue(raw)
    const tokenIndexes = Array.isArray(item?.token_indexes) ? item.token_indexes : null
    if (!tokenIndexes || tokenIndexes.length === 0 || !tokenIndexes.every((tokenIndex) => Number.isSafeInteger(tokenIndex) && tokenIndex >= 0 && tokenIndex < tokens.length)) {
      throw new Error(`Lexical ${index + 1} có token_indexes không hợp lệ.`)
    }
    const normalizedIndexes = [...new Set(tokenIndexes as number[])].sort((left, right) => left - right)
    const key = normalizedIndexes.join(',')
    if (seen.has(key)) throw new Error(`Lexical ${index + 1} lặp đúng cùng token_indexes với một lexical khác.`)
    seen.add(key)
    const expectedText = textFromTokenIndexes(tokens, normalizedIndexes)
    const text = typeof item?.text === 'string' ? item.text.trim() : ''
    if (!text || text !== expectedText) throw new Error(`Lexical ${index + 1} phải có text đúng bằng “${expectedText}”.`)
    const type = typeof item?.type === 'string' && LEXICAL_TYPES.includes(item.type as LexicalType) ? item.type as LexicalType : null
    if (!type) throw new Error(`Lexical ${index + 1} có type không hợp lệ.`)
    const translations = objectValue(item?.translations)
    const vi = typeof translations?.vi === 'string' ? translations.vi.trim() : ''
    if (!vi) throw new Error(`Lexical ${index + 1} thiếu translations.vi.`)
    const phonemes = item?.phonemes === null ? null : typeof item?.phonemes === 'string' ? item.phonemes.trim() || null : null
    return { token_indexes: normalizedIndexes, text, type, translations: { vi }, phonemes }
  })
}
